import React, { ReactNode, useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { Form, Formik, FormikProps } from 'formik';
import useTranslate from '../useTranslate';
import { FormikErrors } from 'formik/dist/types';

export interface EditLargeCardFormikProps<T> extends FormikProps<T> {
    active: boolean;
}

interface Props<T> {
    title?: string;
    submitLabel?: string;
    active_on_init?: boolean;
    optional?: string;
    description?: string;
    handleSubmit: (values: T) => void;
    validationEnabled?: boolean;
    validate?: (values: T) => void | object | Promise<FormikErrors<T>>;
    initialValues: T;
    enableReinitialize?: boolean;
    error: boolean;
    errorElement?: ReactNode;
    handleCancel?: () => void;
    cancelLabel?: string;
    footnote?: string;
    disabled?: boolean;
    children: (formProps: EditLargeCardFormikProps<T>) => ReactNode;

    thirdButton?: boolean;
    thirdButtonLabel?: string;
    handleThirdButton?: () => void;
}

function EditLargeCardFormik<T>(props: Readonly<Props<T>>) {
    useEffect(() => {
        if (props.error) {
            setActive(true);
        }
    }, [props.error]);

    const [active, setActive] = useState(props.active_on_init || props.error);

    const strings = useTranslate('General');

    const _toggleForm = (handleReset: () => void) => {
        if (active) {
            props.handleCancel?.();
            handleReset();
        }
        setActive(!active);
    };
    const divider =
        typeof props.title === 'string' ? <hr className="divider" /> : null;

    function onSubmit(values: T) {
        setActive(false);
        props.handleSubmit(values);
    }
    const showErrorElement = props.errorElement != undefined && props.error;

    return (
        <Row>
            <Col xs={12} sm={12} md={12} lg={12}>
                <div
                    className={
                        'card level-1 clickbox' + (active ? ' card-edit' : '')
                    }
                >
                    <div className="card-content">
                        <div className="card-desc">
                            <h3 className="headline1">
                                {' '}
                                {props.title}{' '}
                                {props.optional && (
                                    <small>{props.optional}</small>
                                )}
                            </h3>
                            {divider}
                            {props.description &&
                                props.description.trim() !== '' &&
                                props.description}
                        </div>
                        <Formik
                            initialValues={props.initialValues}
                            enableReinitialize={props.enableReinitialize}
                            onSubmit={onSubmit}
                            validate={props.validate}
                        >
                            {(formProps: FormikProps<T>) => (
                                <Form>
                                    <div>
                                        {props.children({
                                            ...formProps,
                                            active,
                                        })}
                                    </div>
                                    <div className="clearfix"></div>
                                    {showErrorElement && (
                                        <div className="mb-2">
                                            {props.errorElement}
                                        </div>
                                    )}
                                    {!props.disabled && (
                                        <div className="form-footer">
                                            <Button
                                                type="submit"
                                                disabled={false}
                                                className={
                                                    'btn btn-primary ' +
                                                    (props.thirdButton
                                                        ? ''
                                                        : 'pull-right') +
                                                    (active ? '' : ' hidden')
                                                }
                                            >
                                                {props.submitLabel ||
                                                    strings.save}
                                            </Button>

                                            {active && props.thirdButton && (
                                                <Button
                                                    onClick={() => {
                                                        props.handleThirdButton();
                                                        _toggleForm(
                                                            formProps.handleReset
                                                        );
                                                    }}
                                                    className={
                                                        'btn btn-default ml-2 mr-2'
                                                    }
                                                >
                                                    {props.thirdButtonLabel}
                                                </Button>
                                            )}

                                            {renderCancelButton(
                                                formProps.handleReset
                                            )}
                                        </div>
                                    )}
                                    {props.footnote && (
                                        <div className="form-footnote">
                                            {props.footnote}
                                        </div>
                                    )}
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </Col>
        </Row>
    );

    function renderCancelButton(
        handleReset: (e?: React.SyntheticEvent<never>) => void
    ) {
        if (active && !!props.cancelLabel) {
            return (
                <Button
                    onClick={() => _toggleForm(handleReset)}
                    className={'btn btn-default'}
                >
                    {props.cancelLabel}
                </Button>
            );
        }
        if (active && !props.cancelLabel) {
            return (
                <Button
                    onClick={() => _toggleForm(handleReset)}
                    className={'btn btn-link btn-action btn-close'}
                >
                    {strings.cancel}
                </Button>
            );
        }
        return (
            <Button
                onClick={() => _toggleForm(handleReset)}
                className={'btn btn-link btn-action btn-edit'}
            >
                {strings.edit}
            </Button>
        );
    }
}

export default EditLargeCardFormik;
